import { API } from "@/constants";
import { get, del } from "./api";

export interface SlackStatusResponse {
  connected: boolean;
  company_id: number | null;
  team_id: string | null;
  team_name: string | null;
  installed_at: string | null;
  /** True when the company's Slack questions must come from a linked IQ user (the per-user
   *  sign-in gate). Optional because older backends omit it; treat missing as false. */
  user_auth_required?: boolean;
  /** The IQ bot's Slack user id (e.g. "U0B5VKUQY80"), for a link straight into the bot chat.
   *  Optional because older backends omit it. */
  bot_user_id?: string | null;
}

export interface SlackInstallUrlResponse {
  url: string;
  connected: boolean;
}

export interface SlackDisconnectResponse {
  ok: boolean;
}

/** Backend `app_key` — which Slack app a call manages. */
export type SlackAppKey = "iq" | "pulse";

/**
 * IQ stays on the legacy `/slack/*` routes (they remain the live IQ contract);
 * every other app uses the app-keyed `/slack/apps/{app_key}/*` routes. Both sets
 * share auth, permissions and response shapes.
 */
const endpointsFor = (appKey: SlackAppKey) => (appKey === "iq" ? API.SLACK : API.SLACK.APPS(appKey));

export const getSlackStatus = async (appKey: SlackAppKey = "iq"): Promise<SlackStatusResponse> => {
  return await get<SlackStatusResponse>(endpointsFor(appKey).STATUS);
};

export const getSlackInstallUrl = async (appKey: SlackAppKey = "iq"): Promise<SlackInstallUrlResponse> => {
  // `useSlack` toasts LINK_FETCH_FAILED on failure. Skip the interceptor toast
  // so a 403/5xx does not also show the raw "Request failed with status code".
  return await get<SlackInstallUrlResponse>(endpointsFor(appKey).INSTALL_URL, {
    skipGlobalErrorHandler: true,
  });
};

export interface SlackShareInstallUrlResponse {
  url: string;
  expires_at: string;
}

export const getSlackShareInstallUrl = async (
  appKey: SlackAppKey = "iq",
): Promise<SlackShareInstallUrlResponse> => {
  return await get<SlackShareInstallUrlResponse>(endpointsFor(appKey).SHARE_INSTALL_URL);
};

export const disconnectSlack = async (appKey: SlackAppKey = "iq"): Promise<SlackDisconnectResponse> => {
  return await del<SlackDisconnectResponse>(endpointsFor(appKey).DISCONNECT);
};

/** One provider identity the signed-in person holds in a Slack workspace. */
export interface SlackLinkedIdentity {
  // Nullable on the backend model (`str | None`), so never assume them.
  provider_user_id: string | null;
  provider_workspace_id: string | null;
  workspace_name: string | null;
  display_name: string | null;
  real_name: string | null;
  email: string | null;
  linked_at: string | null;
}

/**
 * The signed-in person's own Slack link state. `workspace_connected` reports the
 * COMPANY's install (the same fact `GET /slack/status` carries); `linked` and
 * `links` are personal. A person can be linked in more than one workspace, so
 * `links` is a list even though the UI treats them as one unit.
 */
export interface SlackUserLinkResponse {
  workspace_connected: boolean;
  linked: boolean;
  links: SlackLinkedIdentity[];
}

export interface SlackUnlinkResponse {
  ok: boolean;
  unlinked: number;
}

/**
 * Read the signed-in person's Slack link. Non-admins may call this.
 *
 * Opted out of the global error toast entirely: the backend answers 503 when the
 * User Service is down, and the row renders its own inline "Could not load your
 * Slack link." for every failure — a toast on top would be duplicate noise. The
 * status-array form (`[503]`) would not cover it either, since a plain network
 * error carries no `error.response` for the interceptor to match on.
 */
export const getSlackUserLink = async (): Promise<SlackUserLinkResponse> => {
  return await get<SlackUserLinkResponse>(API.SLACK.LINK, { skipGlobalErrorHandler: true });
};

/**
 * Clear EVERY Slack link the signed-in person holds; returns how many were cleared.
 *
 * Also opted out of the global error toast: the caller catches the rejection and
 * shows its own "Could not unlink your Slack account…" toast, so leaving the
 * global handler on would pop TWO toasts for one failed click.
 */
export const unlinkSlackUser = async (): Promise<SlackUnlinkResponse> => {
  return await del<SlackUnlinkResponse>(API.SLACK.LINK, { skipGlobalErrorHandler: true });
};
